import React, { useState } from 'react';
import { Asset, Person, Allocation, AssetCategory } from '../types';
import { 
  Trash2, 
  Plus, 
  Minus,
  AlertCircle, 
  Building2, 
  TrendingUp, 
  PiggyBank, 
  Shield, 
  ChevronDown, 
  ShieldCheck, 
  PieChart, 
  UserMinus,
  Edit2,
  Lock,
  Globe,
  Landmark,
  Home,
  Calendar,
  Layers,
  Banknote
} from 'lucide-react';

interface AssetCardProps {
  asset: Asset;
  people: Person[];
  onUpdate: (updatedAsset: Asset) => void;
  onDelete: (id: string) => void;
  onEdit: (asset: Asset) => void;
}

const CATEGORY_CONFIG: Record<string, { icon: any, color: string, bgColor: string }> = {
  [AssetCategory.SAVINGS]: { icon: PiggyBank, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  [AssetCategory.FD]: { icon: Landmark, color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
  [AssetCategory.LOCKER]: { icon: Lock, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  [AssetCategory.INVESTMENT]: { icon: TrendingUp, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  [AssetCategory.INSURANCE]: { icon: Shield, color: 'text-rose-600', bgColor: 'bg-rose-50' },
  [AssetCategory.PROPERTY]: { icon: Home, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
};

export const AssetCard: React.FC<AssetCardProps> = ({ asset, people, onUpdate, onDelete, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const totalAllocated = asset.allocations.reduce((sum, a) => sum + a.percentage, 0);
  
  const config = CATEGORY_CONFIG[asset.category] || { icon: Banknote, color: 'text-gray-600', bgColor: 'bg-gray-50' };
  const Icon = config.icon;

  const handleUpdateAllocation = (personId: string, updates: Partial<Allocation>) => {
    const newAllocations = asset.allocations.map(a => 
      a.personId === personId ? { ...a, ...updates } : a
    );
    onUpdate({ ...asset, allocations: newAllocations });
  };

  return (
    <div className={`bg-white rounded-[20px] transition-all duration-300 overflow-hidden ${isExpanded ? 'ring-2 ring-indigo-100 shadow-xl' : 'hover:bg-gray-50/50'}`}>
      <div className="p-6 flex items-center justify-between gap-6 cursor-pointer group" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center gap-5 flex-1 min-w-0">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${config.bgColor} ${config.color} shadow-sm border border-white`}>
            <Icon size={24} />
          </div>
          <div className="min-w-0 flex-1">
             <div className="flex items-center gap-3">
               <h3 className="text-base font-bold text-[#2B3674] truncate tracking-tight">{asset.name}</h3>
               <span className={`px-2 py-0.5 text-[8px] font-bold uppercase tracking-widest rounded-md ${asset.nominationStatus ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                 {asset.nominationStatus ? 'Verified' : 'Review'}
               </span>
             </div>
             <div className="flex items-center gap-4 mt-1">
                <span className="text-sm font-black text-indigo-600">₹{asset.value.toLocaleString()}</span>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{asset.institution || asset.category}</span>
             </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="hidden sm:flex flex-col items-end">
            <div className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5 ${totalAllocated === 100 ? 'text-emerald-500' : 'text-amber-500'}`}>
              {totalAllocated === 100 ? <ShieldCheck size={14} /> : <AlertCircle size={14} />}
              {totalAllocated}%
            </div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); onEdit(asset); }} className="p-2 text-gray-300 hover:text-indigo-600 transition-colors">
            <Edit2 size={18} />
          </button>
          <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180 text-indigo-600' : 'text-gray-300'}`}>
            <ChevronDown size={20} />
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-6 pb-8 pt-2 animate-fade-in space-y-6">
          <div className="h-1.5 w-full bg-[#F4F7FE] rounded-full overflow-hidden">
            <div className={`h-full transition-all duration-1000 ${totalAllocated === 100 ? 'bg-emerald-500' : 'bg-amber-400'}`} style={{ width: `${totalAllocated}%` }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                <PieChart size={14} className="text-indigo-600" /> Beneficiary Shares
              </h4>
              <div className="space-y-3">
                {asset.allocations.map((alloc) => {
                  const person = people.find(p => p.id === alloc.personId);
                  return (
                    <div key={alloc.personId} className="flex items-center justify-between p-4 bg-[#F4F7FE] rounded-2xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-indigo-600 font-bold text-xs">
                          {person?.fullName.charAt(0)}
                        </div>
                        <span className="text-sm font-bold text-[#2B3674]">{person?.fullName}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-3 bg-white px-3 py-1.5 rounded-xl border border-gray-100">
                          <button onClick={() => handleUpdateAllocation(alloc.personId, { percentage: Math.max(0, alloc.percentage - 5) })} className="p-0.5 hover:text-indigo-600 text-gray-300"><Minus size={14} /></button>
                          <span className="text-xs font-black text-[#2B3674] w-8 text-center">{alloc.percentage}%</span>
                          <button onClick={() => handleUpdateAllocation(alloc.personId, { percentage: Math.min(100, alloc.percentage + 5) })} className="p-0.5 hover:text-indigo-600 text-gray-300"><Plus size={14} /></button>
                        </div>
                        <button onClick={() => onUpdate({...asset, allocations: asset.allocations.filter(a => a.personId !== alloc.personId)})} className="text-gray-300 hover:text-rose-500 transition-colors"><UserMinus size={18} /></button>
                      </div>
                    </div>
                  );
                })}
                <select 
                  onChange={(e) => {
                    if(e.target.value) {
                      onUpdate({...asset, allocations: [...asset.allocations, { personId: e.target.value, percentage: 0 }]});
                      e.target.value = '';
                    }
                  }} 
                  className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl text-xs font-bold text-gray-400 outline-none cursor-pointer hover:border-indigo-100 transition-colors"
                >
                  <option value="">+ Add Nominee from Directory</option>
                  {people.filter(p => !asset.allocations.some(a => a.personId === p.id)).map(p => (
                    <option key={p.id} value={p.id}>{p.fullName}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="bg-[#F4F7FE] p-6 rounded-[20px] space-y-5">
               <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <Building2 size={14} className="text-indigo-600" /> Administrative Details
               </h4>
               <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">Institution</div>
                    <div className="text-xs font-bold text-[#2B3674] truncate">{asset.institution || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">Identifier</div>
                    <div className="text-xs font-bold text-[#2B3674] truncate">{asset.customerId || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">Holding</div>
                    <div className="text-xs font-bold text-[#2B3674]">{asset.modeOfOperation || 'Single'}</div>
                  </div>
                  <div>
                    <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">Allocated Value</div>
                    <div className="text-xs font-bold text-indigo-600">₹{((asset.value * totalAllocated) / 100).toLocaleString()}</div>
                  </div>
               </div>
               <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-start gap-3 p-3 bg-white rounded-xl text-[10px] font-medium text-gray-400 italic">
                    <ShieldCheck size={14} className="text-indigo-300 shrink-0" />
                    Ensure legal forms reflect these shares to avoid future disputes.
                  </div>
               </div>
            </div>
          </div>
          
          <div className="flex justify-end pt-4">
            <button onClick={() => onDelete(asset.id)} className="flex items-center gap-2 text-[10px] font-bold text-rose-500 uppercase tracking-widest hover:bg-rose-50 px-3 py-1.5 rounded-lg transition-colors">
              <Trash2 size={12} /> Delete Record
            </button>
          </div>
        </div>
      )}
    </div>
  );
};